/**
 * Property detail and source-document review navigation.
 */
import { formatInteger } from "./format.js";
import { escapeHtml } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import { leadDetailHtml } from "./dashboard-lead-inventory.js";
import { sourceDocumentReviewHtml } from "./dashboard-source-document-review.js";
import {
  bindEvidenceJumpActions,
  bindSourceDocumentPdfNavigationActions,
  bindSourceDocumentReviewActions,
  bindSourceDocumentReviewDecisionActions,
  bindSourceReviewOcrRetryActions,
  hydrateSourceDocumentInlinePreview
} from "./dashboard-source-review-preview.js";
import {
  bindSourceDocumentClassificationActions,
  bindSourceDocumentPreviewActions
} from "./dashboard-source-document-actions.js";
import { bindFactCorrectionActions, bindTaskAssignmentActions } from "./dashboard-fact-correction-actions.js";
import { bindDownstreamAcquisitionActions } from "./dashboard-downstream-actions.js";
import { bindAcquisitionOfferActions, bindAcquisitionPipelineActions } from "./dashboard-acquisition-actions.js";
import { bindContactOutreachActions } from "./dashboard-contact-outreach-actions.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import type {
  LeadFactoryLeadDetailResponse,
  LeadFactorySourceDocumentReviewResponse,
  SourceDocumentReviewFocus
} from "./dashboard-lead-factory-types.js";

export type LeadDetailNavHost = DashboardActionHost & {
  bindNavigation: (statusElement: HTMLElement) => void;
};

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing dashboard element ${selector}`);
  return element;
}

export async function showLeadDetail(
  leadId: string,
  statusElement: HTMLElement,
  host: LeadDetailNavHost
): Promise<void> {
  host.bindNavigation(statusElement);
  const detail = mustFind<HTMLElement>("#county-detail");
  detail.innerHTML = `
    <div class="detail-inner">
      <div class="detail-header">
        <div>
          <h2>${escapeHtml(leadId)}</h2>
          <p class="subtle">Loading property CRM record...</p>
        </div>
      </div>
    </div>
  `;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}`);
    const body = (await response.json()) as LeadFactoryLeadDetailResponse & { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    detail.innerHTML = leadDetailHtml(body);
    bindSourceDocumentReviewActions(statusElement, (sourceDocumentId, nextStatus, focus) =>
      showSourceDocumentReview(sourceDocumentId, nextStatus, host, focus)
    );
    bindSourceDocumentPreviewActions(statusElement, host);
    bindSourceDocumentClassificationActions(statusElement, host);
    bindFactCorrectionActions(statusElement, host);
    bindTaskAssignmentActions(statusElement, host);
    bindDownstreamAcquisitionActions(statusElement);
    bindAcquisitionPipelineActions(statusElement);
    bindAcquisitionOfferActions(statusElement);
    bindContactOutreachActions(statusElement, host);
    statusElement.textContent = `${leadId}: property detail loaded.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      host.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Lead detail failed.";
  }
}

export async function showSourceDocumentReview(
  sourceDocumentId: string,
  statusElement: HTMLElement,
  host: LeadDetailNavHost,
  focus?: SourceDocumentReviewFocus
): Promise<void> {
  host.bindNavigation(statusElement);
  const detail = mustFind<HTMLElement>("#county-detail");
  detail.innerHTML = `
    <div class="detail-inner">
      <div class="detail-header">
        <div>
          <h2>${escapeHtml(sourceDocumentId)}</h2>
          <p class="subtle">Loading source document review...</p>
        </div>
      </div>
    </div>
  `;
  try {
    const response = await authenticatedFetch(
      `/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/review`
    );
    const body = (await response.json()) as LeadFactorySourceDocumentReviewResponse & { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    detail.innerHTML = sourceDocumentReviewHtml(body, focus);
    const showReview = (id: string, nextStatus: HTMLElement, nextFocus?: SourceDocumentReviewFocus) =>
      showSourceDocumentReview(id, nextStatus, host, nextFocus);
    bindSourceDocumentPreviewActions(statusElement, host);
    bindSourceDocumentPdfNavigationActions(statusElement);
    bindEvidenceJumpActions();
    bindSourceDocumentReviewDecisionActions(body.document.id, statusElement, showReview, host);
    bindSourceReviewOcrRetryActions(body.document.id, statusElement, showReview, host);
    bindTaskAssignmentActions(statusElement, host);
    bindFactCorrectionActions(statusElement, host);
    await hydrateSourceDocumentInlinePreview(body.document.id, statusElement, host, focus?.page);
    statusElement.textContent = focus?.page
      ? `${sourceDocumentId}: source document review loaded at PDF page ${formatInteger(focus.page)}.`
      : `${sourceDocumentId}: source document review loaded.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      host.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Source document review failed.";
  }
}
